/**
 * Advisor cost overrides (QL-20) — an advisor overrides a collection fee for
 * one flow instance (negotiated or client-specific pricing). One value per fee
 * per instance, used in every portfolio of the instance where the fee applies.
 *
 * Whether a fee may be overridden, and whether the override needs a note, is
 * set per fee on the collection (`advisorOverride`), behind the tenant's
 * `cost_overrides` feature flag.
 */

import type { LocalizedString } from "../i18n";
import type { PortfolioCost, PortfolioCostBracket } from "./portfolio-collections";

export const COST_OVERRIDE_MODES = ["off", "allowed", "note_required"] as const;
export type CostOverrideMode = (typeof COST_OVERRIDE_MODES)[number];

export const isCostOverrideMode = (value: unknown): value is CostOverrideMode =>
  (COST_OVERRIDE_MODES as readonly unknown[]).includes(value);

/** A product fee: one product × one collection cost type, always a percent. */
export type ProductCostOverrideTarget = {
  kind: "product_cost";
  productId: string;
  costTypeId: string;
};

/** A portfolio fee row, in that row's own unit (percent, or an amount when the
 * row's costType is "amount"). A tiered row collapses to one flat value. */
export type PortfolioCostOverrideTarget = {
  kind: "portfolio_cost";
  portfolioCostId: string;
};

export type CostOverrideTarget = ProductCostOverrideTarget | PortfolioCostOverrideTarget;
export type CostOverrideKind = CostOverrideTarget["kind"];

export type InstanceCostOverride = CostOverrideTarget & {
  id: string;
  value: number;
  /** The catalogue value when the override was saved; null for a tiered
   * portfolio fee, which has no single original figure. */
  originalValue: number | null;
  note: string | null;
  updatedBy: { id: string; name: string };
  updatedAt: string;
};

export type CostOverridesResponse = { overrides: InstanceCostOverride[] };

/** The caller sends only its own choice; the original value is derived on the
 * server. `note` is required when the fee's mode is "note_required". */
export type SetCostOverrideRequest = CostOverrideTarget & {
  value: number;
  note?: string | null;
};

export type SetCostOverrideResponse = InstanceCostOverride;

/** Attached to an overridden product cost cell. The note is advisor-only:
 * client-facing reads leave it out. */
export type ProductCostOverrideInfo = {
  originalValue: number;
  note?: string | null;
};

/** Attached to an overridden portfolio fee row: the row as the catalogue has it,
 * so a reader can show the original value or tier range struck through. */
export type PortfolioCostOverrideInfo = {
  originalValuePct: number | null;
  originalBrackets: PortfolioCostBracket[];
  note?: string | null;
};

/** A collection fee row as one flow instance sees it: the override (if any)
 * already applied to valuePct / brackets, the catalogue figures in `override`. */
export type InstancePortfolioCost = PortfolioCost & {
  override: PortfolioCostOverrideInfo | null;
};

export type InstancePortfolioCostsResponse = { portfolioCosts: InstancePortfolioCost[] };

/**
 * One fee of an instance's collection, with the ids an override targets and
 * the fee's `advisorOverride` mode. `value` is the figure in force (an override
 * wins); `overrideId` is set when the fee is overridden.
 */
export type CostOverrideFee =
  | (ProductCostOverrideTarget & {
      isin: string | null;
      unit: "pct";
      value: number;
      advisorOverride: CostOverrideMode;
      overrideId: string | null;
    })
  | (PortfolioCostOverrideTarget & {
      nameI18n: LocalizedString;
      unit: "pct" | "amount";
      /** The single figure (manual percent or one-bracket fee); null for a tiered fee. */
      value: number | null;
      brackets: { startValue: number; value: number }[];
      advisorOverride: CostOverrideMode;
      overrideId: string | null;
    });

export type CostOverrideFeesResponse = { fees: CostOverrideFee[] };
